/**
 * IPv4 and IPv6 prefix arithmetic.
 *
 * IPv4 follows RFC 950 (subnetting) and RFC 4632 (CIDR), with the two-address /31 of
 * RFC 3021 and the single-address /32. IPv6 follows RFC 4291 (architecture) with the
 * text form of RFC 5952. Both families share one code path: an address is a BigInt of
 * 32 or 128 bits, so a /0 or a /127 needs no special case and nothing overflows.
 */

import { BIG_ONE, BIG_TWO, BIG_ZERO } from "@/lib/tool-kit/math/bigint";

// biome-ignore-start lint/suspicious/noBitwiseOperators: masks and shifts are this module's whole job

export type Family = 4 | 6;

const OCTET = /^\d{1,3}$/;
const HEX_GROUP = /^[0-9a-f]{1,4}$/;
const DIGITS = /^\d+$/;
const ZONE = /%.*$/;
const WHITESPACE = /\s+/;

const MAX_INPUT = 120;

export function bitsOf(family: Family): number {
  return family === 4 ? 32 : 128;
}

function pow2(exponent: number): bigint {
  return BIG_TWO ** BigInt(exponent);
}

function allOnes(bits: number): bigint {
  return pow2(bits) - BIG_ONE;
}

/** The network mask of a prefix: `prefix` ones, then zeros. */
export function prefixMask(family: Family, prefix: number): bigint {
  const bits = bitsOf(family);
  return allOnes(bits) ^ allOnes(bits - prefix);
}

/** The wildcard (inverse) mask of a prefix, as used in router ACLs. */
export function wildcardMask(family: Family, prefix: number): bigint {
  return allOnes(bitsOf(family) - prefix);
}

// ---------------------------------------------------------------------------
// Parsing
// ---------------------------------------------------------------------------

export type ParseError =
  | "empty"
  | "tooLong"
  | "invalidAddress"
  | "leadingZero"
  | "noPrefix"
  | "invalidPrefix"
  | "invalidMask";

type Quad =
  | { ok: true; value: bigint }
  | { ok: false; error: "invalidAddress" | "leadingZero" };

function parseQuad(text: string): Quad {
  const parts = text.split(".");
  if (parts.length !== 4 || parts.some((part) => !OCTET.test(part))) {
    return { ok: false, error: "invalidAddress" };
  }
  let value = BIG_ZERO;
  for (const part of parts) {
    if (part.length > 1 && part.startsWith("0")) {
      return { ok: false, error: "leadingZero" };
    }
    const octet = Number(part);
    if (octet > 255) {
      return { ok: false, error: "invalidAddress" };
    }
    value = (value << BigInt(8)) | BigInt(octet);
  }
  return { ok: true, value };
}

function parseHexGroups(side: string): number[] | null {
  if (side === "") {
    return [];
  }
  const groups: number[] = [];
  for (const part of side.split(":")) {
    if (!HEX_GROUP.test(part)) {
      return null;
    }
    groups.push(Number.parseInt(part, 16));
  }
  return groups;
}

/** Swap a trailing dotted quad for its two hex groups, or report that it is malformed. */
function expandMappedTail(text: string): string | null {
  const split = text.lastIndexOf(":");
  const tail = text.slice(split + 1);
  if (!tail.includes(".")) {
    return text;
  }
  const quad = parseQuad(tail);
  if (!quad.ok) {
    return null;
  }
  const high = (quad.value >> BigInt(16)).toString(16);
  const low = (quad.value & BigInt(0xff_ff)).toString(16);
  return `${text.slice(0, split + 1)}${high}:${low}`;
}

function groupsToValue(groups: number[]): bigint {
  let value = BIG_ZERO;
  for (const group of groups) {
    value = (value << BigInt(16)) | BigInt(group);
  }
  return value;
}

/** Read an IPv6 address in any RFC 4291 text form, or return null. */
export function parseIPv6(raw: string): bigint | null {
  const lowered = raw.trim().toLowerCase().replace(ZONE, "");
  const text = expandMappedTail(lowered);
  if (text === null || text === "") {
    return null;
  }
  const halves = text.split("::");
  if (halves.length > 2) {
    return null;
  }
  const left = parseHexGroups(halves[0] ?? "");
  const right = parseHexGroups(halves[1] ?? "");
  if (left === null || right === null) {
    return null;
  }
  if (halves.length === 1) {
    return left.length === 8 ? groupsToValue(left) : null;
  }
  const gap = 8 - left.length - right.length;
  if (gap < 1) {
    return null;
  }
  return groupsToValue([...left, ...new Array<number>(gap).fill(0), ...right]);
}

export interface Parsed {
  family: Family;
  address: bigint;
  prefix: number;
}

export type ParseResult =
  | { ok: true; parsed: Parsed }
  | { ok: false; error: ParseError };

function prefixFromMask(maskText: string): number | null {
  const quad = parseQuad(maskText);
  if (!quad.ok) {
    return null;
  }
  for (let prefix = 0; prefix <= 32; prefix++) {
    if (prefixMask(4, prefix) === quad.value) {
      return prefix;
    }
  }
  return null;
}

function prefixFromText(
  text: string,
  family: Family
): { prefix: number } | { error: ParseError } {
  if (family === 4 && text.includes(".")) {
    const prefix = prefixFromMask(text);
    return prefix === null ? { error: "invalidMask" } : { prefix };
  }
  if (!DIGITS.test(text)) {
    return { error: "invalidPrefix" };
  }
  const prefix = Number(text);
  return prefix <= bitsOf(family) ? { prefix } : { error: "invalidPrefix" };
}

function splitAddressAndPrefix(text: string): [string, string | null] {
  const slash = text.indexOf("/");
  if (slash >= 0) {
    return [text.slice(0, slash).trim(), text.slice(slash + 1).trim()];
  }
  const parts = text.split(WHITESPACE);
  if (parts.length === 2 && parts[0] && parts[1]) {
    return [parts[0], parts[1]];
  }
  return [text, null];
}

/**
 * Read `192.168.1.10/24`, `192.168.1.10 255.255.255.0` or `2001:db8::1/48`. The family
 * is chosen by the presence of a colon.
 */
export function parseCidr(input: string): ParseResult {
  const text = input.trim();
  if (text === "") {
    return { ok: false, error: "empty" };
  }
  if (text.length > MAX_INPUT) {
    return { ok: false, error: "tooLong" };
  }
  const family: Family = text.includes(":") ? 6 : 4;
  const [addressText, prefixText] = splitAddressAndPrefix(text);
  let address: bigint | null;
  if (family === 4) {
    const quad = parseQuad(addressText);
    if (!quad.ok) {
      return { ok: false, error: quad.error };
    }
    address = quad.value;
  } else {
    address = parseIPv6(addressText);
  }
  if (address === null) {
    return { ok: false, error: "invalidAddress" };
  }
  if (prefixText === null || prefixText === "") {
    return { ok: false, error: "noPrefix" };
  }
  const prefix = prefixFromText(prefixText, family);
  if ("error" in prefix) {
    return { ok: false, error: prefix.error };
  }
  return { ok: true, parsed: { family, address, prefix: prefix.prefix } };
}

// ---------------------------------------------------------------------------
// Formatting
// ---------------------------------------------------------------------------

export function formatIPv4(value: bigint): string {
  const octets: string[] = [];
  for (let shift = 24; shift >= 0; shift -= 8) {
    octets.push(String(Number((value >> BigInt(shift)) & BigInt(255))));
  }
  return octets.join(".");
}

function groupsOf(value: bigint): number[] {
  const groups: number[] = [];
  for (let shift = 112; shift >= 0; shift -= 16) {
    groups.push(Number((value >> BigInt(shift)) & BigInt(0xff_ff)));
  }
  return groups;
}

/** The longest run of zero groups of length two or more; the first wins a tie (RFC 5952 §4.2). */
function longestZeroRun(groups: number[]): { start: number; length: number } {
  let best = { start: -1, length: 0 };
  let start = -1;
  for (let i = 0; i <= groups.length; i++) {
    if (groups[i] === 0) {
      if (start < 0) {
        start = i;
      }
    } else if (start >= 0) {
      if (i - start > best.length) {
        best = { start, length: i - start };
      }
      start = -1;
    }
  }
  return best.length >= 2 ? best : { start: -1, length: 0 };
}

/** The RFC 5952 canonical text: lower case, no leading zeros, longest zero run as `::`. */
export function formatIPv6(value: bigint): string {
  const groups = groupsOf(value);
  const run = longestZeroRun(groups);
  const hex = (g: number) => g.toString(16);
  if (run.start < 0) {
    return groups.map(hex).join(":");
  }
  const before = groups.slice(0, run.start).map(hex).join(":");
  const after = groups
    .slice(run.start + run.length)
    .map(hex)
    .join(":");
  return `${before}::${after}`;
}

/** All eight groups, four digits each. */
export function expandIPv6(value: bigint): string {
  return groupsOf(value)
    .map((g) => g.toString(16).padStart(4, "0"))
    .join(":");
}

export function formatAddress(family: Family, value: bigint): string {
  return family === 4 ? formatIPv4(value) : formatIPv6(value);
}

/** The dotted IPv4 inside an IPv4-mapped IPv6 address (::ffff:a.b.c.d), or null. */
export function mappedIPv4(value: bigint): string | null {
  const mappedPrefix = BigInt(0xff_ff);
  if (value >> BigInt(32) !== mappedPrefix) {
    return null;
  }
  return formatIPv4(value & BigInt(0xff_ff_ff_ff));
}

/** Dotted binary for IPv4: four groups of eight bits. */
export function binaryIPv4(value: bigint): string {
  const bits = value.toString(2).padStart(32, "0");
  return [0, 8, 16, 24].map((start) => bits.slice(start, start + 8)).join(".");
}

// ---------------------------------------------------------------------------
// Blocks
// ---------------------------------------------------------------------------

export interface Block {
  family: Family;
  prefix: number;
  network: bigint;
  /** The last address of the block (the broadcast address in IPv4 subnets of /30 or larger). */
  last: bigint;
  /** Total addresses in the block, 2^(bits − prefix). */
  size: bigint;
  hostBits: number;
}

export function blockOf(
  family: Family,
  address: bigint,
  prefix: number
): Block {
  const bits = bitsOf(family);
  const network = address & prefixMask(family, prefix);
  const hostBits = bits - prefix;
  return {
    family,
    prefix,
    network,
    last: network | allOnes(hostBits),
    size: pow2(hostBits),
    hostBits,
  };
}

export interface HostRange {
  first: bigint;
  last: bigint;
  /** Addresses a host may use. */
  usable: bigint;
  /** The broadcast address; null when the block has none (/31, /32, IPv6). */
  broadcast: bigint | null;
}

/**
 * Host range under RFC 950 (network and broadcast reserved), RFC 3021 (a /31 has two
 * usable addresses and no broadcast) and the /32 host route. IPv6 has no broadcast, so
 * every address in the prefix is usable.
 */
export function hostRange(block: Block): HostRange {
  if (block.family === 6) {
    return {
      first: block.network,
      last: block.last,
      usable: block.size,
      broadcast: null,
    };
  }
  if (block.prefix >= 31) {
    return {
      first: block.network,
      last: block.last,
      usable: block.size,
      broadcast: null,
    };
  }
  return {
    first: block.network + BIG_ONE,
    last: block.last - BIG_ONE,
    usable: block.size - BIG_TWO,
    broadcast: block.last,
  };
}

export type Role = "network" | "broadcast" | "host";

/** Whether the typed address is the network address, the broadcast address or a host. */
export function roleOf(block: Block, address: bigint): Role {
  if (block.family === 6 || block.prefix >= 31) {
    return "host";
  }
  if (address === block.network) {
    return "network";
  }
  return address === block.last ? "broadcast" : "host";
}

export type ClassicClass = "A" | "B" | "C" | "D" | "E";

/** The pre-CIDR class of an IPv4 address, from its leading bits. Historical only. */
export function classOf(address: bigint): ClassicClass {
  const first = Number(address >> BigInt(24));
  if (first < 128) {
    return "A";
  }
  if (first < 192) {
    return "B";
  }
  if (first < 224) {
    return "C";
  }
  return first < 240 ? "D" : "E";
}

// ---------------------------------------------------------------------------
// Special-purpose ranges
// ---------------------------------------------------------------------------

export type RangeId =
  | "thisNetwork"
  | "private"
  | "sharedSpace"
  | "loopback"
  | "linkLocal"
  | "ietfProtocol"
  | "testNet"
  | "relay6to4"
  | "benchmarking"
  | "multicast"
  | "reserved"
  | "limitedBroadcast"
  | "unspecified"
  | "mapped"
  | "nat64"
  | "nat64Local"
  | "discard"
  | "teredo"
  | "documentation"
  | "sixToFour"
  | "global"
  | "uniqueLocal"
  | "siteLocal";

interface SpecialRange {
  id: RangeId;
  cidr: string;
  rfc: string;
}

/** IPv4 special-purpose blocks: RFC 6890 and the registry entries it points to. */
const SPECIAL_V4: readonly SpecialRange[] = [
  { id: "thisNetwork", cidr: "0.0.0.0/8", rfc: "RFC 1122" },
  { id: "private", cidr: "10.0.0.0/8", rfc: "RFC 1918" },
  { id: "sharedSpace", cidr: "100.64.0.0/10", rfc: "RFC 6598" },
  { id: "loopback", cidr: "127.0.0.0/8", rfc: "RFC 1122" },
  { id: "linkLocal", cidr: "169.254.0.0/16", rfc: "RFC 3927" },
  { id: "private", cidr: "172.16.0.0/12", rfc: "RFC 1918" },
  { id: "ietfProtocol", cidr: "192.0.0.0/24", rfc: "RFC 6890" },
  { id: "testNet", cidr: "192.0.2.0/24", rfc: "RFC 5737" },
  { id: "relay6to4", cidr: "192.88.99.0/24", rfc: "RFC 7526" },
  { id: "private", cidr: "192.168.0.0/16", rfc: "RFC 1918" },
  { id: "benchmarking", cidr: "198.18.0.0/15", rfc: "RFC 2544" },
  { id: "testNet", cidr: "198.51.100.0/24", rfc: "RFC 5737" },
  { id: "testNet", cidr: "203.0.113.0/24", rfc: "RFC 5737" },
  { id: "multicast", cidr: "224.0.0.0/4", rfc: "RFC 5771" },
  { id: "reserved", cidr: "240.0.0.0/4", rfc: "RFC 1112" },
  { id: "limitedBroadcast", cidr: "255.255.255.255/32", rfc: "RFC 919" },
];

/** IPv6 blocks worth naming: RFC 4291 and the IANA special-purpose registry. */
const SPECIAL_V6: readonly SpecialRange[] = [
  { id: "unspecified", cidr: "::/128", rfc: "RFC 4291" },
  { id: "loopback", cidr: "::1/128", rfc: "RFC 4291" },
  { id: "mapped", cidr: "::ffff:0:0/96", rfc: "RFC 4291" },
  { id: "nat64", cidr: "64:ff9b::/96", rfc: "RFC 6052" },
  { id: "nat64Local", cidr: "64:ff9b:1::/48", rfc: "RFC 8215" },
  { id: "discard", cidr: "100::/64", rfc: "RFC 6666" },
  { id: "teredo", cidr: "2001::/32", rfc: "RFC 4380" },
  { id: "documentation", cidr: "2001:db8::/32", rfc: "RFC 3849" },
  { id: "sixToFour", cidr: "2002::/16", rfc: "RFC 3056" },
  { id: "documentation", cidr: "3fff::/20", rfc: "RFC 9637" },
  { id: "global", cidr: "2000::/3", rfc: "RFC 4291" },
  { id: "uniqueLocal", cidr: "fc00::/7", rfc: "RFC 4193" },
  { id: "linkLocal", cidr: "fe80::/10", rfc: "RFC 4291" },
  { id: "siteLocal", cidr: "fec0::/10", rfc: "RFC 3879" },
  { id: "multicast", cidr: "ff00::/8", rfc: "RFC 4291" },
];

export interface RangeHit {
  id: RangeId;
  cidr: string;
  rfc: string;
  /** "inside": the whole block lies in the range. "covers": the block contains the range. */
  relation: "inside" | "covers";
  prefix: number;
}

function rangeBlock(range: SpecialRange): Block {
  const parsed = parseCidr(range.cidr);
  if (!parsed.ok) {
    throw new Error(`bad special range ${range.cidr}`);
  }
  const { family, address, prefix } = parsed.parsed;
  return blockOf(family, address, prefix);
}

/**
 * Special-purpose ranges that overlap a block. CIDR blocks nest or stay apart, so each
 * hit either contains the block or sits inside it. Most specific range first.
 */
export function specialRanges(block: Block): RangeHit[] {
  const table = block.family === 4 ? SPECIAL_V4 : SPECIAL_V6;
  const hits: RangeHit[] = [];
  for (const range of table) {
    const other = rangeBlock(range);
    if (other.last < block.network || other.network > block.last) {
      continue;
    }
    hits.push({
      id: range.id,
      cidr: range.cidr,
      rfc: range.rfc,
      relation: other.prefix <= block.prefix ? "inside" : "covers",
      prefix: other.prefix,
    });
  }
  return hits.sort((a, b) => b.prefix - a.prefix);
}

// ---------------------------------------------------------------------------
// Splitting
// ---------------------------------------------------------------------------

export type SplitMode = "prefix" | "subnets" | "hosts";

export type SplitError = "invalidNumber" | "tooSmall" | "tooFine";

export type SplitPlan =
  | { ok: true; newPrefix: number; count: bigint; requested: number | null }
  | { ok: false; error: SplitError };

/** Bits needed to count `n` items: the smallest b with 2^b ≥ n. */
function ceilLog2(n: number): number {
  let bits = 0;
  while (2 ** bits < n) {
    bits += 1;
  }
  return bits;
}

/**
 * Work out the new prefix from what the reader asked for: a prefix length, a number of
 * subnets (rounded up to a power of two) or a number of hosts per subnet (IPv4 reserves
 * two addresses per subnet, so the host count is padded by two before rounding).
 */
export function planSplit(
  family: Family,
  prefix: number,
  mode: SplitMode,
  value: number
): SplitPlan {
  const bits = bitsOf(family);
  if (!Number.isInteger(value) || value < 1) {
    return { ok: false, error: "invalidNumber" };
  }
  let newPrefix: number;
  if (mode === "prefix") {
    newPrefix = value;
  } else if (mode === "subnets") {
    newPrefix = prefix + ceilLog2(value);
  } else {
    const needed = family === 4 ? value + 2 : value;
    newPrefix = bits - ceilLog2(needed);
  }
  if (newPrefix < prefix) {
    return { ok: false, error: "tooSmall" };
  }
  if (newPrefix > bits) {
    return { ok: false, error: "tooFine" };
  }
  return {
    ok: true,
    newPrefix,
    count: pow2(newPrefix - prefix),
    requested: mode === "prefix" ? null : value,
  };
}

export interface SplitRow {
  index: number;
  block: Block;
  range: HostRange;
}

/** The first `limit` subnets of a block when it is cut at `newPrefix`. */
export function splitBlock(
  block: Block,
  newPrefix: number,
  limit: number
): SplitRow[] {
  const total = pow2(newPrefix - block.prefix);
  const shown = total < BigInt(limit) ? Number(total) : limit;
  const step = pow2(bitsOf(block.family) - newPrefix);
  const rows: SplitRow[] = [];
  for (let i = 0; i < shown; i++) {
    const child = blockOf(
      block.family,
      block.network + BigInt(i) * step,
      newPrefix
    );
    rows.push({ index: i + 1, block: child, range: hostRange(child) });
  }
  return rows;
}

/** `2^n` as the exponent n when the size is a power of two (always, for a CIDR block). */
export function exponentOf(size: bigint): number {
  return size.toString(2).length - 1;
}

// biome-ignore-end lint/suspicious/noBitwiseOperators: see above
